import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { useOnboardingContext } from '@/features/onboarding-v2/context/OnboardingContext';

import { getActiveEditTasks } from '../NoteEditStep.utils';
import { TourArtefactsStep } from './TourArtefactsStep';
import { TourDetailStep } from './TourDetailStep';
import { TourEvidenceStep } from './TourEvidenceStep';
import type { GuidedTourStep } from './guidedTourSteps.utils';

/**
 * The guided-tour arm's replacement for `note-edit`: the same three tasks, one
 * per screen. Both arms write through the same handlers, so the experiment
 * isolates presentation — and both emit the same learn-basics outcome events so
 * the aggregate funnel stays comparable.
 */
export function GuidedTourSteps({ step }: { step: GuidedTourStep }) {
  const { locale } = useIntl();
  const { micTest, analytics, goToNextStep, handleContinue, setSkipOriginStep } =
    useOnboardingContext();

  // This arm drops `note-ready`, so nothing hands the preview from "generating"
  // to the interactive note the way that step's Continue does in control —
  // promote it here as soon as the generation animation finishes.
  const { phase: micPhase, setPhase: setMicPhase } = micTest;
  useEffect(() => {
    if (micPhase !== 'finished') return;
    setMicPhase('editing');
  }, [micPhase, setMicPhase]);

  const completedTasks = () =>
    getActiveEditTasks(locale)
      .filter((task) => micTest.editActivity[task.key])
      .map((task) => task.key);

  const handleFinish = () => {
    analytics.trackLearnBasicsCompleted(completedTasks());
    // Genuine completion: clear any skip origin so the privacy step does not
    // show the skip-recovery Back button.
    setSkipOriginStep(null);
    handleContinue();
  };

  const handleSkip = () => {
    const completedCount = completedTasks().length;
    analytics.trackStepSkipped(step, { tasksCompletedCount: completedCount });
    if (step === 'tour-evidence') {
      analytics.trackLearnBasicsSkipped(completedCount);
    }
    goToNextStep();
  };

  if (step === 'tour-detail') {
    return <TourDetailStep onContinue={handleContinue} onSkip={handleSkip} />;
  }
  if (step === 'tour-artefacts') {
    return <TourArtefactsStep onContinue={handleContinue} onSkip={handleSkip} />;
  }
  return <TourEvidenceStep onContinue={handleFinish} onSkip={handleSkip} />;
}
